const internals = {};

module.exports = {
    clearTwilioRecoveryDebug,
    getTwilioRecoveryDebugState,
    recordTwilioRecoveryEvent,
    runTwilioMessageLoadSimulation,
    startTwilioRecoverySimulation,
    stopTwilioRecoverySimulation
};

function clearTwilioRecoveryDebug() {

    if (internals.activeSimulation?.timer) {
        clearTimeout(internals.activeSimulation.timer);
    }

    internals.activeSimulation = null;
    internals.events = [];
    internals.publishState();
}

function getTwilioRecoveryDebugState() {

    return {
        activeScenario: internals.activeSimulation?.scenario || null,
        events: internals.events.map((event) => ({ ...event }))
    };
}

function recordTwilioRecoveryEvent(event, details = {}) {

    const entry = {
        at: new Date().toISOString(),
        event,
        ...internals.sanitizeDetails(details)
    };

    internals.events = [...internals.events, entry].slice(-internals.MAX_EVENTS);
    internals.publishState();

    const log = internals.warningEvents.has(event) ? console.warn : console.info;

    log('[twilio-recovery]', entry);

    return entry;
}

function runTwilioMessageLoadSimulation({ channelSid } = {}) {

    const simulation = internals.activeSimulation;

    if (!simulation) {
        return Promise.resolve();
    }

    const outcome = simulation.outcomes.shift();
    const remaining = simulation.outcomes.length;

    if (!remaining) {
        clearTimeout(simulation.timer);
        internals.activeSimulation = null;
    }

    recordTwilioRecoveryEvent('simulation_consumed', {
        channelSid,
        outcome,
        remaining,
        scenario: simulation.scenario
    });

    if (outcome === 'stall') {
        return new Promise(() => undefined);
    }

    const error = new Error('Simulated intermittent Twilio connection failure');

    error.code = 'TWILIO_DEBUG_CONNECTION_FAILURE';
    error.isTwilioRecoverySimulation = true;

    return Promise.reject(error);
}

function startTwilioRecoverySimulation({ scenario } = {}) {

    const outcomes = internals.scenarios[scenario];

    if (!outcomes) {
        return false;
    }

    stopTwilioRecoverySimulation();

    internals.activeSimulation = {
        outcomes: [...outcomes],
        scenario,
        timer: setTimeout(stopTwilioRecoverySimulation, internals.SIMULATION_TIMEOUT_MS)
    };

    recordTwilioRecoveryEvent('simulation_armed', {
        remaining: outcomes.length,
        scenario
    });

    return true;
}

function stopTwilioRecoverySimulation() {

    if (!internals.activeSimulation) {
        return false;
    }

    const { scenario, timer } = internals.activeSimulation;

    clearTimeout(timer);
    internals.activeSimulation = null;
    recordTwilioRecoveryEvent('simulation_stopped', { scenario });

    return true;
}

internals.MAX_EVENTS = 50;
internals.SIMULATION_TIMEOUT_MS = 60_000;
internals.activeSimulation = null;
internals.events = [];
internals.scenarios = {
    'intermittent-failure': ['failure'],
    'persistent-failure': ['failure', 'failure', 'failure'],
    'socket-stall': ['stall']
};
internals.warningEvents = new Set([
    'client_recovery_failed',
    'connection_error',
    'load_failed',
    'load_timeout',
    'recovery_failed',
    'simulation_consumed'
]);

internals.sanitizeDetails = (details = {}) => {

    const sanitized = {};
    const allowedKeys = [
        'attempt',
        'channelSid',
        'elapsedMs',
        'forceRecreate',
        'mode',
        'outcome',
        'phase',
        'remaining',
        'scenario',
        'state'
    ];

    allowedKeys.forEach((key) => {

        if (typeof details[key] !== 'undefined') {
            sanitized[key] = details[key];
        }
    });

    if (details.error) {
        sanitized.error = {
            code: details.error.code ?? details.error.errorCode ?? null,
            message: String(details.error.message || details.error),
            name: details.error.name || null
        };
    }

    return sanitized;
};

internals.publishState = () => {

    if (typeof window !== 'undefined') {
        window.__nearpeerTwilioRecoveryDebug = getTwilioRecoveryDebugState();
    }
};
